import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { SEOHead } from "@components/organisms";
import { Button, Text } from "@components/atoms";
import { ROUTES } from "@shared/constants";
import { realPaymentService } from "../services/payment.service";
import { trackPurchase } from "@services/analytics/analytics";
import { confirmPurchase, type ConfirmPurchasePayload } from "@services/seatMap.service";
import { esRetornoDeInscripcionHyrun, urlDeRetornoHyrun } from "../hyrunReturn";

/**
 * RF-7 (bug-20261001-venta-cerrada-se-lee-como-pagada) — `expired` es un estado PROPIO.
 *
 * No reusa `error`, que le dice al comprador que espere porque la confirmación puede seguir en
 * curso; ni `rejected`, que le dice que el banco le negó el pago. En un carrito vencido las dos
 * cosas son falsas: no hay nada que esperar y el banco nunca vio la operación.
 */
type PaymentStatus = "loading" | "success" | "rejected" | "expired" | "error";

/**
 * Payment return page.
 * PayPhone redirects here after a successful payment attempt.
 * URL: /payment/return?id={orderId}&clientTransactionId={clientTxId}
 *
 * Mirrors the web_ticketin flow: POST /consultar_payphone with both IDs
 * so the backend verifies the payment server-side with PayPhone's API.
 */
export function PaymentReturnPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // PayPhone passes both params on redirect
  const orderId = searchParams.get("id");
  const clientTransactionId = searchParams.get("clientTransactionId");

  // Una inscripción de Hyrun no es una venta de esta web: vuelve acá solo porque PayPhone decide
  // el retorno por tienda y no por lo que se le pasa en la cajita. Ver `hyrunReturn.ts`.
  const esDeHyrun = esRetornoDeInscripcionHyrun(clientTransactionId);

  // Need both IDs to verify with backend; missing either → error immediately
  const [status, setStatus] = useState<PaymentStatus>(() =>
    orderId && clientTransactionId ? "loading" : "error",
  );

  // Se rebota ANTES de consultar nada: acá no hay venta que encontrar y lo único que salía era un
  // "No se encontró la venta" sobre un cobro ya hecho. `replace` y no `assign` para que el botón
  // de volver del navegador no traiga a la persona de regreso a esta página.
  useEffect(() => {
    if (esDeHyrun) window.location.replace(urlDeRetornoHyrun(window.location.search));
  }, [esDeHyrun]);

  useEffect(() => {
    if (!orderId || !clientTransactionId || esDeHyrun) return;

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    /**
     * Verifica el pago con tolerancia a fallos transitorios
     * (bug-20260604-payphone-falso-error).
     *
     * Problema: el widget de PayPhone a veces falla su verify-otp interno
     * (status 0 por red/CORS) o consultar_payphone devuelve un timeout puntual,
     * PERO el pago SÍ se procesa y el backend confirma la venta segundos después.
     * Mostrar "Error en el pago" tras una ventana corta es un falso negativo que
     * asusta al cliente (que sí pagó y recibió tickets) y provoca dobles cobros.
     *
     * V1 usaba 5 intentos × 2.5 s ≈ 12.5 s, pero la confirmación real
     * PayPhone → backend tarda mucho más: medido en prod 60–113 s (04/06/2026,
     * ventas 118952 y 118956). La ventana corta se rendía antes de que el
     * backend confirmara, mostrando un error falso.
     *
     * Solución: extender el polling a un presupuesto total de 180 s (margen sobre
     * los 113 s máximos observados) con backoff. En cada iteración:
     *   1) consultar_payphone (verificación activa server-side), y
     *   2) get_sale_by_id como red de seguridad (state===0 ⟺ estadopago=APROBADO,
     *      correlación verificada en BD el 04/06/2026). Si PayPhone aprueba O la
     *      orden quedó confirmada → éxito inmediato. Solo al agotar el
     *      presupuesto sin confirmación → error.
     */
    const verifyPaymentWithRetry = async (): Promise<
      "success" | "rejected" | "expired" | "timeout"
    > => {
      const TOTAL_BUDGET_MS = 180_000; // cubre la latencia real de PayPhone (≤113 s) con margen
      const FAST_INTERVAL_MS = 2_500; // primeros intentos: capturar confirmación inmediata
      const FAST_ATTEMPTS = 4; // ~10 s en modo rápido antes de pasar al intervalo estable
      const SLOW_INTERVAL_MS = 5_000; // resto de la ventana: ~5 s para no saturar backend/PayPhone
      const deadline = Date.now() + TOTAL_BUDGET_MS;

      let attempt = 0;
      while (Date.now() < deadline) {
        // 1) Verificación server-side activa contra PayPhone
        const result = await realPaymentService.consultarPayphone(orderId, clientTransactionId);
        if (result.success) return "success";

        // Rechazo DEFINITIVO confirmado por PayPhone (status 500 "no fue aprobada"):
        // cortar de una. NO usar el respaldo get_sale_by_id, porque devuelve la venta
        // con state:0 aunque el pago haya sido rechazado → daba un falso "Pago exitoso".
        if (result.rejected) return "rejected";

        // 2) Red de seguridad SOLO ante fallo transitorio de consultar_payphone
        //    (red/CORS/timeout): ¿el backend ya confirmó la venta vía webhook?
        // bugfix-20260714: get_sale_by_id espera el numero de orden de ticketin
        // (clientTransactionId), no el id numerico de PayPhone (orderId) — con
        // el id equivocado este chequeo nunca encontraba la venta.
        const statusRes = await realPaymentService.getStatus(clientTransactionId);
        if (statusRes.success && statusRes.data.status === "PAGADO") return "success";

        // RF-7 — una venta VENCIDA o ANULADA es definitiva: no hay nada que seguir esperando, y
        // agotar los 180 s para terminar en "seguimos verificando" solo le hace creer al
        // comprador que su pago todavía puede entrar.
        //
        // Deliberadamente NO se corta ante RECHAZADO: el backend puede marcarlo mientras PayPhone
        // todavía está confirmando, y ahí un corte temprano daría un falso rechazo sobre un pago
        // que sí entró — el mismo falso negativo que bug-20260604-payphone-falso-error. El rechazo
        // sigue viniendo de PayPhone (`result.rejected`), que es quien lo sabe.
        if (
          statusRes.success &&
          (statusRes.data.status === "EXPIRADO" || statusRes.data.status === "ANULADO")
        ) {
          return "expired";
        }

        attempt += 1;
        const interval = attempt <= FAST_ATTEMPTS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS;
        if (Date.now() + interval >= deadline) break; // no dormir más allá del presupuesto
        await sleep(interval);
      }
      return "timeout";
    };

    const verifyAll = async () => {
      // Verify primary order from URL params (con reintentos + verificación de estado)
      const outcome = await verifyPaymentWithRetry();
      if (outcome === "rejected") {
        setStatus("rejected");
        return;
      }
      if (outcome === "expired") {
        setStatus("expired");
        return;
      }
      if (outcome === "timeout") {
        setStatus("error");
        return;
      }

      // Confirm secondary orders from multi-locality purchases.
      // consultar_payphone cannot be used here: the backend looks up the sale by
      // the clientTransactionId returned from PayPhone, which always resolves to
      // the primary order. Instead, call confirm_sale directly — the combined
      // PayPhone transaction already collected payment for all localities.
      const extraOrdersRaw = sessionStorage.getItem("pending_extra_orders");
      sessionStorage.removeItem("pending_extra_orders");
      if (extraOrdersRaw) {
        const extraOrderIds: string[] = JSON.parse(extraOrdersRaw);
        for (const extraOrderId of extraOrderIds) {
          await realPaymentService.confirmSale(extraOrderId);
        }
      }

      setStatus("success");

      // Confirmar asientos numerados (katia-20260401-asientos-numerados).
      // CheckoutModal guardó el contexto en sessionStorage antes de redirigir a PayPhone.
      const numeratedRaw = sessionStorage.getItem("numerated_purchase_context");
      sessionStorage.removeItem("numerated_purchase_context");
      if (numeratedRaw) {
        try {
          const numeratedCtx = JSON.parse(numeratedRaw) as ConfirmPurchasePayload;
          await confirmPurchase(numeratedCtx);
        } catch (err) {
          // No bloquear el éxito de pago — el backend tiene idempotencia y el
          // job de expiración liberará los asientos si el confirm falla definitivamente.
          console.error("seat confirm failed:", err);
        }
      }

      // Leer contexto guardado por CheckoutModal antes de la redirección a PayPhone
      type PurchaseContext = {
        eventId: string;
        eventName: string;
        valueCents: number;
        paymentMethod: string;
        items?: Array<{ localityId: string; localityName: string; price: number; comision: number; quantity: number }>;
      };
      let purchaseContext: PurchaseContext | null = null;
      try {
        const raw = sessionStorage.getItem("purchase_context");
        if (raw) purchaseContext = JSON.parse(raw) as PurchaseContext;
      } catch { /* ignorar */ }
      sessionStorage.removeItem("purchase_context");

      trackPurchase({
        transactionId: clientTransactionId,
        eventId: purchaseContext?.eventId ?? orderId,
        eventName: purchaseContext?.eventName ?? "TicketIn",
        value: purchaseContext ? purchaseContext.valueCents / 100 : 0,
        paymentMethod: purchaseContext?.paymentMethod ?? "PAYPHONE",
        items: purchaseContext?.items,
      });
    };

    void verifyAll().catch(() => setStatus("error"));
  }, [esDeHyrun, orderId, clientTransactionId]);

  const handleGoToTickets = () => {
    navigate(ROUTES.MY_TICKETS);
  };

  const handleGoHome = () => {
    navigate(ROUTES.HOME);
  };

  return (
    <>
      <SEOHead title="Confirmación de Pago" noIndex />
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-neutral-60 to-primary-110 px-4">
        <div className="flex w-full max-w-md flex-col items-center gap-6 rounded-2xl bg-white p-8 shadow-lg">
          {status === "loading" && (
            <>
              <Loader2 size={80} className="animate-spin text-primary-110" />
              <Text variant="h3" as="h1" className="text-center text-black">
                Verificando pago...
              </Text>
              <Text variant="body" className="text-center text-neutral-110">
                Por favor espera mientras confirmamos tu transacción. Esto puede
                tardar hasta un par de minutos.
              </Text>
              {/* bug-20260604-payphone-falso-error: prevenir doble cobro —
                  el cliente NO debe reintentar mientras la confirmación está en curso */}
              <Text variant="body" className="text-center text-sm font-medium text-amber-600">
                No cierres esta página ni vuelvas a pagar.
              </Text>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle size={80} className="text-green-500" />
              <Text variant="h3" as="h1" className="text-center text-black">
                ¡Pago exitoso!
              </Text>
              <Text variant="body" className="text-center text-neutral-110">
                Tu compra ha sido procesada correctamente. Tus tickets están
                listos.
              </Text>
              {clientTransactionId && (
                <Text variant="body" className="text-sm text-neutral-80">
                  ID de transacción: {clientTransactionId}
                </Text>
              )}
              <div className="flex w-full flex-col gap-3 pt-4">
                <Button
                  className="w-full h-12 bg-primary-110 text-white hover:bg-primary-100"
                  onClick={handleGoToTickets}
                >
                  Ver mis tickets
                </Button>
                <Button
                  className="w-full h-12 bg-transparent border border-primary-110 text-primary-110 hover:bg-primary-110/10"
                  onClick={handleGoHome}
                >
                  Volver al inicio
                </Button>
              </div>
            </>
          )}

          {status === "rejected" && (
            <>
              <XCircle size={80} className="text-red-500" />
              <Text variant="h3" as="h1" className="text-center text-black">
                Pago rechazado
              </Text>
              <Text variant="body" className="text-center text-neutral-110">
                Tu pago no fue aprobado por PayPhone, así que no se generó ninguna
                compra ni se realizó ningún cobro. Puedes intentar de nuevo con otra
                tarjeta o método de pago.
              </Text>
              {clientTransactionId && (
                <Text variant="body" className="text-sm text-neutral-80">
                  ID de transacción: {clientTransactionId}
                </Text>
              )}
              <div className="flex w-full flex-col gap-3 pt-4">
                <Button
                  className="w-full h-12 bg-primary-110 text-white hover:bg-primary-100"
                  onClick={handleGoHome}
                >
                  Volver al inicio
                </Button>
              </div>
            </>
          )}

          {/* RF-7 — la compra venció. Ni "esperá" (pantalla de error) ni "tu banco lo negó"
              (pantalla de rechazo): no hay nada en curso y el banco nunca vio la operación. Lo
              único accionable es volver a comprar, así que ese es el botón principal. */}
          {status === "expired" && (
            <>
              <AlertTriangle size={80} className="text-amber-500" />
              <Text variant="h3" as="h1" className="text-center text-black">
                La compra venció
              </Text>
              <Text variant="body" className="text-center text-neutral-110">
                Pasó el tiempo para completar el pago, así que liberamos tus entradas y{" "}
                <span className="font-semibold">no se realizó ningún cobro</span>. Si
                todavía quedan disponibles, puedes volver a comprarlas.
              </Text>
              {clientTransactionId && (
                <Text variant="body" className="text-sm text-neutral-80">
                  ID de transacción: {clientTransactionId}
                </Text>
              )}
              <div className="flex w-full flex-col gap-3 pt-4">
                <Button
                  className="w-full h-12 bg-primary-110 text-white hover:bg-primary-100"
                  onClick={handleGoHome}
                >
                  Volver al inicio
                </Button>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              {/* bug-20260604-payphone-falso-error: no afirmar categóricamente que
                  el pago falló — la confirmación de PayPhone puede seguir en curso.
                  Mensaje que evita inducir al cliente a pagar dos veces. */}
              <AlertTriangle size={80} className="text-amber-500" />
              <Text variant="h3" as="h1" className="text-center text-black">
                Seguimos verificando tu pago
              </Text>
              <Text variant="body" className="text-center text-neutral-110">
                Aún no pudimos confirmar tu transacción. Si el dinero fue
                debitado,{" "}
                <span className="font-semibold">no vuelvas a pagar</span>: tus
                tickets pueden tardar unos minutos en aparecer en "Mis tickets".
                Si no llegan, contacta a soporte.
              </Text>
              <div className="flex w-full flex-col gap-3 pt-4">
                <Button
                  className="w-full h-12 bg-primary-110 text-white hover:bg-primary-100"
                  onClick={handleGoToTickets}
                >
                  Ver mis tickets
                </Button>
                <Button
                  className="w-full h-12 bg-transparent border border-primary-110 text-primary-110 hover:bg-primary-110/10"
                  onClick={handleGoHome}
                >
                  Volver al inicio
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
